<script setup>
import { ref, computed, onMounted, watch } from "vue";
import Modal from "bootstrap/js/dist/modal";
import { useContactStore } from "@/stores/contactStore";
import { useCrmClientStore } from "@/stores/crmClientStore";
import { useUserStore } from "@/stores/userStore";
import { useDealStore } from "@/stores/dealStore";
import { toast } from "@/utils/toast";

const props = defineProps({
  editItem: { type: Object, default: null },
});
const emit = defineEmits(["saved"]);

const contactStore = useContactStore();
const clientStore = useCrmClientStore();
const userStore = useUserStore();
const dealStore = useDealStore();

const keyForce = ref(0);
const isEdit = computed(() => !!props.editItem?.id);

const statusOptions = ["Active", "Inactive"];
const clients = computed(() =>
  Array.isArray(clientStore.clients) ? clientStore.clients : []
);

const owners = computed(() => {
  const raw = Array.isArray(userStore.users)
    ? userStore.users
    : Array.isArray(userStore.users?.data)
    ? userStore.users.data
    : [];

  return raw.map((u) => ({
    ...u,
    full_name: u?.first_name
      ? `${u.first_name} ${u.last_name || ""}`.trim()
      : u?.email || "User",
  }));
});

// Active tab (so we can show one footer + smooth UX)
const activeTab = ref("basic");

// Deal labels cache (to avoid flicker + improve UX)
const dealLabelMap = ref({});

function blankForm() {
  return {
    id: null,
    client_id: null,
    first_name: "",
    last_name: "",
    job_title: "",
    company_name: "",
    email: "",
    phone: "",
    phone_alt: "",

    owner_id: null,
    status: "Active",
    rating: null,
    source: "",
    birthday: "",

    address: "",
    city: "",
    state: "",
    country: "",
    zipcode: "",

    facebook: "",
    twitter: "",
    linkedin: "",
    skype: "",
    whatsapp: "",
    instagram: "",

    tags: [],
    notes: "",

    deal_ids: [],
  };
}

const form = ref(blankForm());

function reset() {
  Object.assign(form.value, blankForm());
  keyForce.value++;
  activeTab.value = "basic";
  linkedDealsQuery.value = "";
  linkedDealsOptions.value = [];
}

// ---------- OWNER DROPDOWN (same pattern as Deals) ----------
const ownerQuery = ref("");
const ownerOptions = ref([]); // [{ id, label }]
const ownerLoading = ref(false);

// cache labels
const ownerLabelMap = ref({});

const setOwnerLabel = (id, label) => {
  if (!id) return;
  ownerLabelMap.value[id] = label;
};

const getOwnerLabel = (id) => {
  return (
    ownerLabelMap.value[id] ||
    userStore.users?.find((u) => u.id === id)?.first_name ||
    (id != null ? `User #${id}` : "Owner")
  );
};

function ownerToOption(u) {
  const label = u.first_name
    ? `${u.first_name} ${u.last_name || ""}`.trim()
    : u.email;

  setOwnerLabel(u.id, label);
  return { id: u.id, label };
}

// Debounced fetch (local or server)
const fetchOwners = debounce(async (q = "", perPage = 20) => {
  ownerLoading.value = true;
  try {
    // If users already loaded → filter locally
    let rows = userStore.users || [];

    if (q) {
      const qq = q.toLowerCase();
      rows = rows.filter(
        (u) =>
          u.first_name?.toLowerCase().includes(qq) ||
          u.last_name?.toLowerCase().includes(qq) ||
          u.email?.toLowerCase().includes(qq)
      );
    }

    ownerOptions.value = rows.slice(0, perPage).map(ownerToOption);
  } finally {
    ownerLoading.value = false;
  }
}, 250);

function openOwnerDropdown() {
  fetchOwners("", 10);
}

function onOwnerInput() {
  const q = ownerQuery.value.trim();
  if (q.length === 0) fetchOwners("", 10);
  else if (q.length >= 2) fetchOwners(q, 20);
}

function selectOwner(opt) {
  form.value.owner_id = opt.id;
  setOwnerLabel(opt.id, opt.label);
}

// const owners = computed(() => {
//   const raw = Array.isArray(userStore.users)
//     ? userStore.users
//     : Array.isArray(userStore.users?.data)
//     ? userStore.users.data
//     : [];
//   return raw;
// });

// function nameByUser(u) {
//   return u?.first_name
//     ? `${u.first_name} ${u.last_name || ""}`.trim()
//     : u?.email || "User";
// }

function hide() {
  const el = document.getElementById("add_contact");
  const inst = el
    ? Modal.getInstance(el) || Modal.getOrCreateInstance(el)
    : null;
  inst?.hide();
}

function hideDelete() {
  const el = document.getElementById("delete_contact");
  const inst = el
    ? Modal.getInstance(el) || Modal.getOrCreateInstance(el)
    : null;
  inst?.hide();
}

async function confirmDelete() {
  try {
    if (!props.editItem?.id) return;
    await contactStore.remove(props.editItem.id);
    hideDelete();
    emit("saved");
  } catch (e) {
    console.error(e);
  }
}

function toPayload(f) {
  return {
    client_id: f.client_id || null,
    first_name: f.first_name?.trim(),
    last_name: f.last_name?.trim() || null,
    job_title: f.job_title?.trim() || null,
    company_name: f.company_name?.trim() || null,

    email: f.email?.trim() || null,
    phone: f.phone?.trim() || null,
    phone_alt: f.phone_alt?.trim() || null,

    owner_id: f.owner_id || null,
    status: f.status || "Active",
    rating: f.rating != null ? Number(f.rating) : null,
    source: f.source?.trim() || null,
    birthday: f.birthday || null,

    address: f.address?.trim() || null,
    city: f.city?.trim() || null,
    state: f.state?.trim() || null,
    country: f.country?.trim() || null,
    zipcode: f.zipcode?.trim() || null,

    facebook: f.facebook?.trim() || null,
    twitter: f.twitter?.trim() || null,
    linkedin: f.linkedin?.trim() || null,
    skype: f.skype?.trim() || null,
    whatsapp: f.whatsapp?.trim() || null,
    instagram: f.instagram?.trim() || null,

    tags: Array.isArray(f.tags) ? [...f.tags].filter(Boolean) : [],
    notes: f.notes?.trim() || null,

    deal_ids: Array.isArray(f.deal_ids) ? [...f.deal_ids] : [],
  };
}

function hydrate(val) {
  if (!val) {
    reset();
    return;
  }

  form.value.id = val.id ?? null;
  form.value.client_id = val.client_id ?? val.client?.id ?? null;
  form.value.first_name = val.first_name ?? "";
  form.value.last_name = val.last_name ?? "";
  form.value.job_title = val.job_title ?? "";
  form.value.company_name = val.company_name ?? "";

  form.value.email = val.email ?? "";
  form.value.phone = val.phone ?? "";
  form.value.phone_alt = val.phone_alt ?? "";

  form.value.owner_id = val.owner_id ?? null;
  form.value.status = val.status ?? "Active";
  form.value.rating = val.rating ?? null;
  form.value.source = val.source ?? "";
  form.value.birthday = val.birthday ?? "";

  form.value.address = val.address ?? "";
  form.value.city = val.city ?? "";
  form.value.state = val.state ?? "";
  form.value.country = val.country ?? "";
  form.value.zipcode = val.zipcode ?? "";

  form.value.facebook = val.facebook ?? "";
  form.value.twitter = val.twitter ?? "";
  form.value.linkedin = val.linkedin ?? "";
  form.value.skype = val.skype ?? "";
  form.value.whatsapp = val.whatsapp ?? "";
  form.value.instagram = val.instagram ?? "";

  form.value.tags = Array.isArray(val.tags)
    ? [...val.tags]
    : (val.tags ?? "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

  form.value.notes = val.notes ?? "";

  form.value.deal_ids = Array.isArray(val.deals)
    ? val.deals.map((d) => d.id)
    : Array.isArray(val.deal_ids)
    ? [...val.deal_ids]
    : [];

  if (Array.isArray(val.deals)) {
    val.deals.forEach((d) =>
      setDealLabel(d.id, d.name || d.title || `Deal #${d.id}`)
    );
  }

  keyForce.value++;
  ensureSelectedDealsLabeled();
}

// ensure data before hydrate
watch(
  () => props.editItem,
  async (val) => {
    if (!clientStore.clients?.length) await clientStore.ensureClients({ per_page: 200 });
    if (!userStore.users?.length) await userStore.ensureUsers({ per_page: 200 });
    if (!dealStore.items?.length) await dealStore.ensureList({ per_page: 200 });
    hydrate(val);
  },
  { immediate: true }
);

watch(
  () => form.value.client_id,
  (clientId) => {
    const selected = clients.value.find((item) => Number(item.id) === Number(clientId));
    if (selected) {
      form.value.company_name = selected.name || "";
    }
  }
);

async function submit() {
  if (!form.value.first_name?.trim())
    return toast("First name is required", "error");
  if (form.value.email && !/^\S+@\S+\.\S+$/.test(form.value.email)) {
    return toast("Email looks invalid", "error");
  }

  const payload = toPayload(form.value);
  try {
    if (isEdit.value && form.value.id) {
      await contactStore.update(form.value.id, payload);
    } else {
      await contactStore.create(payload);
    }
    hide();
    emit("saved");
  } catch {
    /* store toasts */
  }
}

// ---------- debounce ----------
function debounce(fn, ms = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

// ---------- smartFetch for deals ----------
async function smartFetchDeals(params = {}) {
  const fn =
    dealStore.list ||
    dealStore.search ||
    dealStore.load ||
    dealStore.fetch ||
    dealStore.fetchAll;
  if (!fn) return [];
  const resp = await fn({
    q: String(params?.q ?? ""),
    per_page: params?.per_page ?? 20,
    page: params?.page ?? 1,
  });
  const out = resp?.data?.data ?? resp?.data ?? resp ?? dealStore.items ?? [];
  return Array.isArray(out) ? out : [];
}

// ---------- Linked Deals ----------
const linkedDealsQuery = ref("");
const linkedDealsOptions = ref([]); // [{id, label}]
const linkedDealsLoading = ref(false);

// const setDealLabel = (id, label) => {
//   if (!id) return;
//   dealLabelMap.value = { ...dealLabelMap.value, [id]: label || `Deal #${id}` };
// };

const setDealLabel = (id, label) => {
  if (!id) return;
  dealLabelMap.value[id] = label || `Deal #${id}`;
};

// ✅ build a fast index once (O(n)) then lookups are O(1)
const dealIndex = computed(() => {
  const idx = Object.create(null);

  // store items
  (dealStore.items || []).forEach((d) => {
    if (!d?.id) return;
    idx[d.id] = d.name || d.title || `Deal #${d.id}`;
  });

  // cached labels override
  Object.assign(idx, dealLabelMap.value || {});
  return idx;
});

const getDealLabel = (id) => {
  return (
    dealLabelMap.value?.[id] ||
    dealStore?.byId?.[id]?.name ||
    dealStore?.byId?.[id]?.title ||
    (dealStore.items || []).find((d) => d.id === id)?.name ||
    (dealStore.items || []).find((d) => d.id === id)?.title ||
    (id != null ? `Deal #${id}` : "Deal")
  );
};

function dealToOption(d) {
  const label = d.name || d.title || `Deal #${d.id}`;
  setDealLabel(d.id, label);
  return { id: d.id, label };
}

// const linkedDealsSelected = computed(() => {
//   const ids = Array.isArray(form.value.deal_ids) ? form.value.deal_ids : [];
//   return ids.map((id) => ({ id, label: getDealLabel(id) }));
// });

const linkedDealsSelected = computed(() => {
  const ids = Array.isArray(form.value.deal_ids) ? form.value.deal_ids : [];
  const idx = dealIndex.value;
  return ids.map((id) => ({ id, label: idx[id] || `Deal #${id}` }));
});

/**
 * ✅ PERFORMANCE: keep dropdown DOM light
 * - Only render up to 30 options (enough for UX)
 * - avoids lag if API returns huge arrays
 */
const linkedDealsOptionsTrimmed = computed(() =>
  (linkedDealsOptions.value || []).slice(0, 30)
);

const fetchLinkedDeals = debounce(async (q = "", perPage = 20) => {
  linkedDealsLoading.value = true;
  try {
    const rows = await smartFetchDeals({ q, per_page: perPage, page: 1 });
    linkedDealsOptions.value = rows.filter((r) => r && r.id).map(dealToOption);
  } catch (e) {
    console.error(e);
  } finally {
    linkedDealsLoading.value = false;
  }
}, 250);

function onLinkedDealsInput() {
  const q = (linkedDealsQuery.value || "").trim();
  if (!q) fetchLinkedDeals("", 12);
  else if (q.length >= 2) fetchLinkedDeals(q, 20);
}

function openLinkedDealsDropdown() {
  // seed quickly; very fast UI
  fetchLinkedDeals("", 12);
}

function toggleDeal(opt) {
  setDealLabel(opt.id, opt.label);
  const arr = Array.isArray(form.value.deal_ids)
    ? [...form.value.deal_ids]
    : [];
  const idx = arr.indexOf(opt.id);
  if (idx === -1) arr.push(opt.id);
  else arr.splice(idx, 1);
  form.value.deal_ids = arr;
}

function removeSelectedDeal(id) {
  form.value.deal_ids = (form.value.deal_ids || []).filter((x) => x !== id);
}

function ensureSelectedDealsLabeled() {
  const ids = Array.isArray(form.value.deal_ids) ? form.value.deal_ids : [];
  if (!ids.length) return;

  ids.forEach((id) => {
    if (!dealLabelMap.value[id]) setDealLabel(id, `Deal #${id}`);
  });

  ids.forEach(async (id) => {
    try {
      const rows = await smartFetchDeals({
        q: String(id),
        per_page: 5,
        page: 1,
      });
      const hit = rows.find((r) => r.id === id);
      if (hit) setDealLabel(id, hit.name || hit.title || `Deal #${id}`);
    } catch {}
  });
}

watch(
  () => dealStore.items,
  () => ensureSelectedDealsLabeled(),
  { deep: false }
);

watch(
  () => [form.value.owner_id, form.value.status],
  ([o, s]) => {
    console.time("owner/status change render cost");
    requestAnimationFrame(() => {
      console.timeEnd("owner/status change render cost");
    });
  }
);

// Tags UX
const tagDraft = ref("");
function addTag() {
  const v = (tagDraft.value || "").trim();
  if (!v) return;
  const exists = (form.value.tags || []).some(
    (t) => String(t).toLowerCase() === v.toLowerCase()
  );
  if (!exists) form.value.tags = [...(form.value.tags || []), v];
  tagDraft.value = "";
}
function removeTag(t) {
  form.value.tags = (form.value.tags || []).filter((x) => x !== t);
}

onMounted(() => {
  const el = document.getElementById("add_contact");
  el?.addEventListener("show.bs.modal", async () => {
    if (!dealStore.items?.length) await dealStore.ensureList({ per_page: 200 });
    if (isEdit.value) hydrate(props.editItem);
    else reset();
  });
  el?.addEventListener("hidden.bs.modal", reset);
});
</script>

<template>
  <!-- Add/Edit Contact Modal -->
  <div class="modal fade dq-modal" id="add_contact" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-xl dq-dialog">
      <div class="modal-content dq-modal-content" :key="keyForce">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-indigo">
              <i class="ti ti-user-circle"></i>
            </div>
            <div>
              <h4 class="modal-title dq-title mb-0">
                {{ isEdit ? "Edit Contact" : "Add New Contact" }}
              </h4>
              <div class="dq-subtitle">
                Capture contact details, link deals, and track context in one
                place.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submit">
          <!-- Tabs (sticky inside modal) -->
          <div class="dq-tabs">
            <ul class="nav nav-underline dq-nav" role="tablist">
              <li class="nav-item" role="presentation">
                <button
                  class="nav-link"
                  :class="{ active: activeTab === 'basic' }"
                  type="button"
                  @click="activeTab = 'basic'"
                >
                  <i class="ti ti-id me-2"></i>Basic
                </button>
              </li>
              <li class="nav-item" role="presentation">
                <button
                  class="nav-link"
                  :class="{ active: activeTab === 'address' }"
                  type="button"
                  @click="activeTab = 'address'"
                >
                  <i class="ti ti-map-pin me-2"></i>Address
                </button>
              </li>
              <li class="nav-item" role="presentation">
                <button
                  class="nav-link"
                  :class="{ active: activeTab === 'social' }"
                  type="button"
                  @click="activeTab = 'social'"
                >
                  <i class="ti ti-world me-2"></i>Social
                </button>
              </li>
            </ul>
          </div>

          <!-- Body (scrolls) -->
          <div class="dq-body">
            <!-- BASIC -->
            <div v-show="activeTab === 'basic'" class="dq-pane">
              <div class="row g-3">
                <div class="col-md-4">
                  <label class="form-label dq-label"
                    >First Name <span class="text-danger">*</span></label
                  >
                  <input
                    v-model.trim="form.first_name"
                    type="text"
                    class="form-control dq-input"
                    required
                  />
                </div>

                <div class="col-md-4">
                  <label class="form-label dq-label">Last Name</label>
                  <input
                    v-model.trim="form.last_name"
                    type="text"
                    class="form-control dq-input"
                  />
                </div>

                <div class="col-md-4">
                  <label class="form-label dq-label">Job Title</label>
                  <input
                    v-model.trim="form.job_title"
                    type="text"
                    class="form-control dq-input"
                    placeholder="e.g. Procurement Manager"
                  />
                </div>

                <div class="col-md-4">
                  <label class="form-label dq-label">Account</label>
                  <select v-model="form.client_id" class="form-select dq-input">
                    <option :value="null">Select account</option>
                    <option
                      v-for="client in clients"
                      :key="client.id"
                      :value="client.id"
                    >
                      {{ client.name }}
                    </option>
                  </select>
                </div>

                <div class="col-md-4">
                  <label class="form-label dq-label">Account Name</label>
                  <input
                    v-model.trim="form.company_name"
                    type="text"
                    class="form-control dq-input"
                    :readonly="!!form.client_id"
                    :placeholder="
                      form.client_id
                        ? 'Synced from selected account'
                        : 'e.g. Acme Ltd'
                    "
                  />
                </div>

                <div class="col-md-4">
                  <label class="form-label dq-label">Email</label>
                  <input
                    v-model.trim="form.email"
                    type="email"
                    class="form-control dq-input"
                    placeholder="name@company.com"
                  />
                </div>

                <div class="col-md-4">
                  <label class="form-label dq-label">Phone</label>
                  <input
                    v-model.trim="form.phone"
                    type="text"
                    class="form-control dq-input"
                    placeholder="+254..."
                  />
                </div>

                <div class="col-md-4">
                  <label class="form-label dq-label">Phone (Alt)</label>
                  <input
                    v-model.trim="form.phone_alt"
                    type="text"
                    class="form-control dq-input"
                  />
                </div>

                <div class="mb-3">
                  <label class="form-label">Owner</label>

                  <!-- Selected -->
                  <div class="mb-2">
                    <span v-if="form.owner_id" class="badge bg-light text-dark">
                      {{ getOwnerLabel(form.owner_id) }}
                      <i
                        class="ti ti-x ms-1"
                        role="button"
                        @click="form.owner_id = null"
                      ></i>
                    </span>
                    <span v-else class="text-muted">No owner selected</span>
                  </div>

                  <!-- Dropdown -->
                  <div
                    class="dropdown w-100"
                    data-bs-auto-close="outside"
                    data-bs-display="static"
                  >
                    <button
                      class="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center"
                      type="button"
                      data-bs-toggle="dropdown"
                      @click="openOwnerDropdown"
                    >
                      <span class="text-truncate">Search owner…</span>
                      <i class="ti ti-chevron-down ms-2"></i>
                    </button>

                    <div class="dropdown-menu p-2 w-100 shadow">
                      <input
                        v-model="ownerQuery"
                        @input="onOwnerInput"
                        type="text"
                        class="form-control mb-2"
                        placeholder="Search users…"
                        autocomplete="off"
                      />

                      <div class="search-list">
                        <div
                          v-if="ownerLoading"
                          class="text-center py-2 small text-muted"
                        >
                          Loading…
                        </div>

                        <button
                          v-for="opt in ownerOptions"
                          :key="'owner-' + opt.id"
                          type="button"
                          class="dropdown-item d-flex justify-content-between align-items-center"
                          @click="selectOwner(opt)"
                        >
                          <span class="text-truncate">{{ opt.label }}</span>
                          <i
                            class="ti"
                            :class="
                              form.owner_id === opt.id ? 'ti-check' : 'ti-user'
                            "
                          ></i>
                        </button>

                        <div
                          v-if="!ownerLoading && !ownerOptions.length"
                          class="text-center py-2 small text-muted"
                        >
                          No results
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-4">
                  <label class="form-label">Status</label>
                  <select v-model="form.status" class="form-select">
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>

                <div class="col-md-4">
                  <label class="form-label dq-label">Rating (0—5)</label>
                  <input
                    v-model.number="form.rating"
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    class="form-control dq-input"
                  />
                </div>

                <div class="col-md-8">
                  <label class="form-label dq-label">Source</label>
                  <input
                    v-model.trim="form.source"
                    class="form-control dq-input"
                    placeholder="Phone, Social media, Referral..."
                  />
                </div>

                <!-- Tags -->
                <div class="col-12">
                  <label
                    class="form-label dq-label d-flex align-items-center justify-content-between"
                  >
                    <span>Tags</span>
                    <small class="text-muted">Press Enter to add</small>
                  </label>

                  <div class="dq-tags">
                    <div class="dq-tags-input">
                      <i class="ti ti-tag"></i>
                      <input
                        v-model.trim="tagDraft"
                        class="form-control dq-input dq-tagfield"
                        placeholder="e.g. VIP, Hot Lead, Procurement..."
                        @keydown.enter.prevent="addTag"
                      />
                      <button
                        type="button"
                        class="btn btn-light dq-mini"
                        @click="addTag"
                      >
                        Add
                      </button>
                    </div>

                    <div class="dq-chips" v-if="(form.tags || []).length">
                      <span v-for="t in form.tags" :key="t" class="dq-chip">
                        {{ t }}
                        <i
                          class="ti ti-x"
                          role="button"
                          @click="removeTag(t)"
                        ></i>
                      </span>
                    </div>

                    <div v-else class="text-muted small">No tags added</div>
                  </div>
                </div>

                <!-- Linked Deals -->
                <div class="col-12">
                  <label class="form-label dq-label">Linked Deals</label>

                  <!-- Selected chips -->
                  <div class="dq-chips mb-2">
                    <span
                      v-for="sel in linkedDealsSelected"
                      :key="sel.id"
                      class="dq-chip dq-chip-soft"
                    >
                      <i class="ti ti-briefcase me-1"></i>
                      {{ sel.label }}
                      <i
                        class="ti ti-x"
                        role="button"
                        @click="removeSelectedDeal(sel.id)"
                      ></i>
                    </span>

                    <span
                      v-if="!linkedDealsSelected.length"
                      class="text-muted small"
                    >
                      None selected
                    </span>
                  </div>

                  <!-- Searchable dropdown -->
                  <div
                    class="dropdown w-100"
                    data-bs-auto-close="outside"
                    data-bs-display="static"
                  >
                    <button
                      class="btn dq-dealbtn w-100 d-flex justify-content-between align-items-center"
                      type="button"
                      data-bs-toggle="dropdown"
                      @click="openLinkedDealsDropdown"
                    >
                      <span class="text-truncate"> Add / Search deals… </span>
                      <i class="ti ti-chevron-down ms-2"></i>
                    </button>

                    <div class="dropdown-menu p-2 w-100 shadow dq-dropdown">
                      <div class="dq-search">
                        <i class="ti ti-search"></i>
                        <!-- ✅ PERF: lazy reduces reactive churn -->
                        <input
                          v-model.lazy="linkedDealsQuery"
                          @input="onLinkedDealsInput"
                          type="text"
                          class="form-control dq-input dq-search-input"
                          placeholder="Search deals (min 2 chars)…"
                          autocomplete="off"
                        />
                        <span
                          v-if="linkedDealsLoading"
                          class="dq-spin spinner-border spinner-border-sm"
                        ></span>
                      </div>

                      <div class="dq-list">
                        <button
                          v-for="opt in linkedDealsOptionsTrimmed"
                          :key="'deal-' + opt.id"
                          type="button"
                          class="dropdown-item dq-item d-flex justify-content-between align-items-center"
                          @click="toggleDeal(opt)"
                        >
                          <span class="text-truncate">{{ opt.label }}</span>
                          <i
                            class="ti"
                            :class="
                              (form.deal_ids || []).includes(opt.id)
                                ? 'ti-check'
                                : 'ti-plus'
                            "
                          ></i>
                        </button>

                        <div
                          v-if="
                            !linkedDealsLoading &&
                            !linkedDealsOptionsTrimmed.length
                          "
                          class="text-center py-3 small text-muted"
                        >
                          No results
                        </div>

                        <div
                          v-if="(linkedDealsOptions.value || []).length > 30"
                          class="text-center pt-2 small text-muted"
                        >
                          Showing first 30 results. Refine your search to narrow
                          down.
                        </div>
                      </div>
                    </div>
                  </div>

                  <small class="text-muted"
                    >Optional: associate this contact with deals.</small
                  >
                </div>

                <div class="col-12">
                  <label class="form-label dq-label">Notes</label>
                  <textarea
                    v-model.trim="form.notes"
                    rows="3"
                    class="form-control dq-input dq-textarea"
                  ></textarea>
                </div>
              </div>
            </div>

            <!-- ADDRESS -->
            <div v-show="activeTab === 'address'" class="dq-pane">
              <div class="row g-3">
                <div class="col-12">
                  <label class="form-label dq-label">Address</label>
                  <input
                    v-model.trim="form.address"
                    type="text"
                    class="form-control dq-input"
                  />
                </div>
                <div class="col-md-6">
                  <label class="form-label dq-label">City</label>
                  <input
                    v-model.trim="form.city"
                    type="text"
                    class="form-control dq-input"
                  />
                </div>
                <div class="col-md-6">
                  <label class="form-label dq-label">State</label>
                  <input
                    v-model.trim="form.state"
                    type="text"
                    class="form-control dq-input"
                  />
                </div>
                <div class="col-md-6">
                  <label class="form-label dq-label">Country</label>
                  <input
                    v-model.trim="form.country"
                    type="text"
                    class="form-control dq-input"
                  />
                </div>
                <div class="col-md-6">
                  <label class="form-label dq-label">Zipcode</label>
                  <input
                    v-model.trim="form.zipcode"
                    type="text"
                    class="form-control dq-input"
                  />
                </div>
              </div>
            </div>

            <!-- SOCIAL -->
            <div v-show="activeTab === 'social'" class="dq-pane">
              <div class="row g-3">
                <div
                  class="col-md-6"
                  v-for="f in [
                    'facebook',
                    'twitter',
                    'linkedin',
                    'skype',
                    'whatsapp',
                    'instagram',
                  ]"
                  :key="f"
                >
                  <label class="form-label dq-label text-capitalize">{{
                    f
                  }}</label>
                  <input
                    v-model.trim="form[f]"
                    type="text"
                    class="form-control dq-input"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Sticky Footer (single footer, smooth UX) -->
          <div class="modal-footer dq-footer">
            <button
              type="button"
              class="btn btn-light dq-btn dq-btn-muted"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>

            <button
              type="submit"
              class="btn btn-primary dq-btn dq-btn-primary"
              :disabled="contactStore.loading"
            >
              <span
                v-if="contactStore.loading"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              {{ isEdit ? "Save Changes" : "Save Contact" }}
              <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Delete Contact Modal -->
  <div class="modal fade dq-modal" id="delete_contact" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered dq-del-dialog">
      <div class="modal-content dq-modal-content">
        <div class="modal-header dq-modal-header dq-danger-head">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-red">
              <i class="ti ti-trash"></i>
            </div>
            <div>
              <h5 class="modal-title dq-title mb-0">Delete Contact</h5>
              <div class="dq-subtitle">This action cannot be undone.</div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <div class="modal-body dq-body">
          <div class="dq-danger">
            <i class="ti ti-alert-octagon"></i>
            <div>
              <div class="dq-danger-title">
                You’re about to delete this contact.
              </div>
              <div class="dq-danger-sub">
                If it’s linked to deals, those links will be removed.
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer dq-footer">
          <button
            type="button"
            class="btn btn-light dq-btn dq-btn-muted"
            data-bs-dismiss="modal"
          >
            Cancel
          </button>

          <button
            class="btn btn-danger dq-btn dq-btn-danger"
            :disabled="contactStore.loading"
            @click="confirmDelete"
          >
            <span
              v-if="contactStore.loading"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            Delete
            <i class="ti ti-trash ms-2"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ========= World-class modal shell ========= */
.dq-dialog {
  max-width: 1140px;
}
.dq-del-dialog {
  max-width: 640px;
}
@media (max-width: 992px) {
  .dq-dialog,
  .dq-del-dialog {
    max-width: 96%;
  }
}

.dq-modal-content {
  border-radius: 22px;
  border: none;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.08),
    0 40px 90px rgba(15, 23, 42, 0.14);
}

.dq-modal-header {
  padding: 18px 22px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, rgba(99, 102, 241, 0.14), #fff);
}
.dq-danger-head {
  background: linear-gradient(180deg, rgba(239, 68, 68, 0.12), #fff);
}

.dq-titlewrap {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dq-title {
  font-weight: 950;
  letter-spacing: -0.35px;
}
.dq-subtitle {
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}
.dq-close {
  opacity: 0.9;
}
.dq-close:hover {
  opacity: 1;
}

.dq-hero {
  width: 46px;
  height: 46px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}
.dq-hero-indigo {
  background: rgba(99, 102, 241, 0.16);
  color: rgb(79, 70, 229);
}
.dq-hero-red {
  background: rgba(239, 68, 68, 0.14);
  color: rgb(220, 38, 38);
}

/* ========= Tabs ========= */
.dq-tabs {
  padding: 10px 22px 0 22px;
  background: #fff;
  position: sticky;
  top: 0;
  z-index: 2;
}
.dq-nav .nav-link {
  font-weight: 900;
  color: rgba(15, 23, 42, 0.65);
  border-radius: 999px;
  padding: 10px 14px;
}
.dq-nav .nav-link.active {
  color: rgba(15, 23, 42, 0.9);
  background: rgba(15, 23, 42, 0.04);
}

/* ========= Body scrolling ========= */
.dq-body {
  padding: 16px 22px 18px 22px;
  max-height: calc(100vh - 270px); /* ✅ smooth scroll */
  overflow: auto;
}
.dq-pane {
  animation: dqfade 0.12s ease;
}
@keyframes dqfade {
  from {
    opacity: 0.7;
    transform: translateY(2px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

/* ========= Inputs ========= */
.dq-label {
  font-size: 0.82rem;
  font-weight: 900;
}
.dq-input {
  border-radius: 14px;
  padding: 10px 12px;
  transition: all 0.15s ease;
}
.dq-input:focus {
  border-color: rgba(99, 102, 241, 0.55);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.14);
}
.dq-textarea {
  font-family: ui-sans-serif, system-ui;
}

/* ========= Tags ========= */
.dq-tags {
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: rgba(15, 23, 42, 0.02);
  border-radius: 16px;
  padding: 12px;
}
.dq-tags-input {
  display: flex;
  align-items: center;
  gap: 10px;
}
.dq-tags-input i {
  color: rgba(15, 23, 42, 0.55);
}
.dq-tagfield {
  flex: 1;
}
.dq-mini {
  border-radius: 12px;
  font-weight: 900;
  padding: 8px 12px;
}

/* ========= Chips ========= */
.dq-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.dq-chip {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 7px 12px;
  border-radius: 999px;
  font-weight: 900;
  font-size: 0.8rem;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: #fff;
  color: rgba(15, 23, 42, 0.82);
}
.dq-chip i {
  cursor: pointer;
  opacity: 0.85;
}
.dq-chip i:hover {
  opacity: 1;
}
.dq-chip-soft {
  background: rgba(99, 102, 241, 0.07);
  border-color: rgba(99, 102, 241, 0.16);
}

/* ========= Deals dropdown (no lag) ========= */
.dq-dealbtn {
  border-radius: 14px;
  padding: 10px 12px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: #fff;
}
.dq-dropdown {
  border-radius: 16px;
  border: 1px solid rgba(15, 23, 42, 0.08);
}
.dq-search {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 6px 10px 6px;
}
.dq-search i {
  color: rgba(15, 23, 42, 0.55);
}
.dq-search-input {
  flex: 1;
}
.dq-spin {
  margin-right: 6px;
}

.dq-list {
  max-height: 280px;
  overflow: auto;
  padding-right: 4px;
}
.dq-item {
  border-radius: 12px;
  padding: 10px 10px;
}
.dq-item:active {
  background: rgba(99, 102, 241, 0.1);
}

/* ========= Footer (sticky) ========= */
.dq-footer {
  padding: 14px 22px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
  position: sticky;
  bottom: 0;
  z-index: 2;
}
.dq-btn {
  border-radius: 12px;
  padding: 10px 16px;
  font-weight: 900;
}
.dq-btn-primary {
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.1);
}
.dq-btn-danger {
  box-shadow: 0 12px 24px rgba(239, 68, 68, 0.12);
}

/* ========= Delete warning ========= */
.dq-danger {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px;
  border-radius: 18px;
  border: 1px solid rgba(239, 68, 68, 0.14);
  background: rgba(239, 68, 68, 0.08);
}
.dq-danger i {
  font-size: 18px;
  color: rgb(220, 38, 38);
  margin-top: 2px;
}
.dq-danger-title {
  font-weight: 950;
}
.dq-danger-sub {
  margin-top: 6px;
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.7);
}
</style>
